import { AdminLayout } from "./AdminLayout";
import { Plus, Search, MoreVertical, Globe, Building2 } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '', website: '', industry: '', size: '', address: '', notes: ''
  });

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/crm/companies`, { withCredentials: true });
      setCompanies(response.data);
    } catch (error) {
      toast.error("Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompanies(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await axios.put(`${API}/crm/companies/${editingCompany.company_id}`, formData, { withCredentials: true });
        toast.success("Company updated");
      } else {
        await axios.post(`${API}/crm/companies`, formData, { withCredentials: true });
        toast.success("Company created");
      }
      setIsDialogOpen(false);
      setEditingCompany(null);
      setFormData({ name: '', website: '', industry: '', size: '', address: '', notes: '' });
      fetchCompanies();
    } catch (error) {
      toast.error("Failed to save company");
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name, website: company.website || '', industry: company.industry || '',
      size: company.size || '', address: company.address || '', notes: company.notes || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    try {
      await axios.delete(`${API}/crm/companies/${companyId}`, { withCredentials: true });
      toast.success("Company deleted");
      fetchCompanies();
    } catch (error) {
      toast.error("Failed to delete company");
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.industry || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout title="Companies">
      <div className="space-y-6" data-testid="admin-companies">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Companies</h2>
            <p className="text-sm text-[#8A8A8A]">{companies.length} total</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2"
                onClick={() => { setEditingCompany(null); setFormData({ name: '', website: '', industry: '', size: '', address: '', notes: '' }); }}
                data-testid="add-company-btn">
                <Plus className="w-4 h-4" /> Add Company
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
              <DialogHeader><DialogTitle>{editingCompany ? 'Edit Company' : 'Add New Company'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <input type="text" placeholder="Company Name *" required value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-field" />
                <input type="url" placeholder="Website" value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})} className="input-field" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Industry" value={formData.industry}
                    onChange={(e) => setFormData({...formData, industry: e.target.value})} className="input-field" />
                  <select value={formData.size} onChange={(e) => setFormData({...formData, size: e.target.value})} className="input-field">
                    <option value="">Company Size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                </div>
                <input type="text" placeholder="Address" value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})} className="input-field" />
                <textarea placeholder="Notes" rows={3} value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})} className="input-field resize-none" />
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm">{editingCompany ? 'Update' : 'Create'} Company</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
          <input type="text" placeholder="Search companies..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} className="input-field pl-10 w-full sm:w-96" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="card-dashboard animate-pulse">
                <div className="h-6 bg-[#2E2E2E] rounded w-3/4 mb-4" />
                <div className="h-4 bg-[#2E2E2E] rounded w-1/2 mb-2" />
                <div className="h-4 bg-[#2E2E2E] rounded w-2/3" />
              </div>
            ))
          ) : filteredCompanies.length === 0 ? (
            <div className="col-span-full text-center py-12 text-[#8A8A8A]">No companies found</div>
          ) : (
            filteredCompanies.map((company) => (
              <div key={company.company_id} className="card-dashboard" data-testid={`company-card-${company.company_id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#5F8FB8]/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#5F8FB8]" />
                    </div>
                    <div>
                      <h3 className="font-bold">{company.name}</h3>
                      {company.industry && <p className="text-sm text-[#8A8A8A]">{company.industry}</p>}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 hover:bg-[#2E2E2E]"><MoreVertical className="w-4 h-4" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                      <DropdownMenuItem onClick={() => handleEdit(company)} className="cursor-pointer hover:bg-[#2E2E2E]">Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(company.company_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {company.website && (
                  <a href={company.website} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-[#5F8FB8] flex items-center gap-1 mb-2 hover:underline">
                    <Globe className="w-3 h-3" /> {company.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
                {company.size && <p className="text-sm text-[#8A8A8A]">{company.size} employees</p>}
                {company.address && <p className="text-sm text-[#8A8A8A] mt-2">{company.address}</p>}
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Companies;
